import { Player, Round, Question, Challenge, GameItem } from '../types';
import { shouldBeChallenge } from './challengeUtils';

/**
 * Generate a random 6-digit room code
 */
export function generateRoomCode(): string {
  return Math.floor(100000 + Math.random() * 900000).toString();
}

/**
 * Validate room code format (6 digits)
 */
export function isValidRoomCode(code: string): boolean {
  return /^\d{6}$/.test(code);
}

/**
 * Calculate voting results for a round
 */
export interface PlayerResult {
  player: Player;
  votes: number;
  percentage: number;
  isWinner: boolean;
}

export function calculateResults(players: Player[], tally: Record<string, number>): PlayerResult[] {
  const totalVotes = Object.values(tally).reduce((sum, votes) => sum + votes, 0);
  const maxVotes = Math.max(...Object.values(tally), 0);
  
  return players.map(player => {
    const votes = tally[player.id] || 0;
    const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;

    // Normally a player wins if they got the (non-zero) top vote count.
    let isWinner = votes > 0 && votes === maxVotes;

    // Fun rule: if nobody voted at all in a round, EVERYONE "wins" (i.e.
    // everybody drinks). This is UI/animation only — the authoritative stored
    // `round.winners` is still empty for a 0-vote round.
    if (totalVotes === 0) {
      isWinner = true;
    }

    return {
      player,
      votes,
      percentage,
      isWinner
    };
  });
}

/**
 * Calculate overall game winners based on total votes across all rounds
 */
export function calculateOverallWinners(
  players: Player[], 
  rounds: Round[]
): { winners: Player[]; totalVotes: Record<string, number> } {
  const totalVotes: Record<string, number> = {};
  
  // Initialize vote counts
  players.forEach(player => {
    totalVotes[player.id] = 0;
  });
  
  // Sum votes across all rounds
  rounds.forEach(round => {
    Object.entries(round.tally).forEach(([playerId, votes]) => {
      if (totalVotes[playerId] !== undefined) {
        totalVotes[playerId] += votes;
      }
    });
  });
  
  const maxTotalVotes = Math.max(...Object.values(totalVotes), 0);
  const winners = players.filter(player => totalVotes[player.id] === maxTotalVotes && maxTotalVotes > 0);
  
  return { winners, totalVotes };
}

/**
 * Get random subset of questions
 */
export function getRandomQuestions<T>(questions: T[], count: number): T[] {
  const shuffled = [...questions].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Format time remaining as MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Validate nickname
 */
export function isValidNickname(nickname: string): boolean {
  return nickname.trim().length >= 2 && nickname.trim().length <= 20;
}

/**
 * Get nickname validation error message
 */
export function getNicknameError(nickname: string): string | null {
  const trimmed = nickname.trim();
  if (trimmed.length === 0) {
    return 'El nombre es obligatorio';
  }
  if (trimmed.length < 2) {
    return 'El nombre debe tener al menos 2 caracteres';
  }
  if (trimmed.length > 20) {
    return 'El nombre no puede tener más de 20 caracteres';
  }
  return null;
}

/**
 * Check if room can start (minimum players requirement)
 */
export function canStartGame(players: Player[]): boolean {
  const connectedPlayers = players.filter(p => p.connected);
  return connectedPlayers.length >= 2;
}

/**
 * Get game start error message
 */
export function getGameStartError(players: Player[]): string | null {
  const connectedPlayers = players.filter(p => p.connected);
  
  if (connectedPlayers.length < 2) {
    return 'Se necesitan al menos 2 jugadores para empezar';
  }
  
  return null;
}

/**
 * Resolve template variables in question text
 */
export function resolveTemplate(questionText: string, players: Player[]): string {
  let resolved = questionText;
  
  // Extract all template variables from the text
  const templateMatches = questionText.match(/\{\{\w+\}\}/g) || [];
  
  templateMatches.forEach(match => {
    const variable = match.replace(/[{}]/g, '');
    
    if (variable === 'user') {
      // Select random player for {{user}} variable
      const connectedPlayers = players.filter(p => p.connected);
      if (connectedPlayers.length > 0) {
        const randomPlayer = connectedPlayers[Math.floor(Math.random() * connectedPlayers.length)];
        resolved = resolved.replace(match, randomPlayer.nickname);
      } else {
        resolved = resolved.replace(match, 'alguien');
      }
    }
    // Add more variable types here in the future (e.g., {{randomPlayer}}, {{host}}, etc.)
  });
  
  return resolved;
}

/**
 * Check if a question is a template
 */
export function isTemplateQuestion(question: Question): boolean {
  return !!question.template && /\{\{\w+\}\}/.test(question.template);
}

/**
 * Extract template variables from question text
 */
export function extractTemplateVariables(questionText: string): string[] {
  const matches = questionText.match(/\{\{(\w+)\}\}/g) || [];
  return matches.map(match => match.replace(/[{}]/g, ''));
}

/**
 * Get random subset of challenges
 */
export function getRandomChallenges(challenges: Challenge[], count: number): Challenge[] {
  const shuffled = [...challenges].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

/**
 * Create a game sequence with questions and challenges mixed
 */
export function createGameSequence(
  questions: Question[],
  challenges: Challenge[],
  totalRounds: number,
  challengePercentage: number = 30
): GameItem[] {
  const sequence: GameItem[] = [];
  const targetChallenges = Math.ceil((totalRounds * challengePercentage) / 100);
  const targetQuestions = totalRounds - targetChallenges;

  console.log('🎮 Creating game sequence:', {
    totalRounds,
    targetQuestions,
    targetChallenges,
    availableQuestions: questions.length,
    availableChallenges: challenges.length
  });

  // Get random questions and challenges
  const selectedQuestions = getRandomQuestions(questions, targetQuestions);
  const selectedChallenges = getRandomChallenges(challenges, targetChallenges);

  console.log('🎲 Selected items:', {
    questionsSelected: selectedQuestions.length,
    challengesSelected: selectedChallenges.length,
    selectedQuestions: selectedQuestions.map(q => q.id),
    selectedChallenges: selectedChallenges.map(c => c.id)
  });

  // Critical check: Do we actually have challenges?
  if (targetChallenges > 0 && selectedChallenges.length === 0) {
    console.error('❌ CRITICAL: Expected challenges but none selected!', {
      targetChallenges,
      availableChallenges: challenges.length,
      challenges: challenges.map(c => ({ id: c.id, category: c.category, active: c.active }))
    });
  }

  let questionIndex = 0;
  let challengeIndex = 0;

  // Create the sequence, distributing challenges throughout
  for (let i = 0; i < totalRounds; i++) {
    const shouldUseChallenge = shouldBeChallenge(i, totalRounds, challengePercentage);
    
    console.log(`🎯 Round ${i} decision:`, {
      shouldUseChallenge,
      challengeIndex,
      selectedChallengesLength: selectedChallenges.length,
      canUseChallenge: shouldUseChallenge && challengeIndex < selectedChallenges.length
    });
    
    if (shouldUseChallenge && challengeIndex < selectedChallenges.length) {
      sequence.push(selectedChallenges[challengeIndex]);
      challengeIndex++;
      console.log(`🔥 Round ${i}: Adding challenge - ${selectedChallenges[challengeIndex - 1]?.id}`);
    } else if (questionIndex < selectedQuestions.length) {
      sequence.push(selectedQuestions[questionIndex]);
      questionIndex++;
      console.log(`❓ Round ${i}: Adding question - ${selectedQuestions[questionIndex - 1]?.id}`);
    } else {
      // CRITICAL FIX: No fallback cycling to prevent duplicates
      console.error(`❌ Not enough unique content for ${totalRounds} rounds!`, {
        availableQuestions: selectedQuestions.length,
        availableChallenges: selectedChallenges.length,
        totalAvailable: selectedQuestions.length + selectedChallenges.length,
        roundsRequested: totalRounds
      });
      break; // Stop adding items to prevent duplicates
    }
  }

  console.log('✅ Final game sequence:', {
    totalItems: sequence.length,
    questions: sequence.filter(item => !isGameItemChallenge(item)).length,
    challenges: sequence.filter(item => isGameItemChallenge(item)).length
  });

  return sequence.slice(0, totalRounds);
}

/**
 * Check if a game item is a challenge
 */
export function isGameItemChallenge(item: GameItem): item is Challenge {
  return !!(item && typeof item === 'object' && item.id && typeof item.id === 'string' && item.id.startsWith('c_'));
}